import * as Sentry from "@sentry/react";
import { useDispatch } from "react-redux";
import { displayErrorMessage } from "store/actions/utilActions";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { LocalizationHelperInstance, DateHelper } from "utils/helpers";
import { useWintSalaryService } from "serviceHooks";
import useGrossSalaryChangeDialog from "./useGrossSalaryChangeDialog";
import useGrossSalaryChangePermissions from "./useGrossSalaryChangePermissions";
import { MoreActionMenuItem } from "models/propTypes/menuPropTypes";

type BuildSalaryChangeActionsArgs = {
  personId: number;
  yearMonth: number;
  personName: string;
  onActionSuccess?: () => void;
  hiddenZero?: boolean;
  hiddenAdjust?: boolean;
};

function useSalaryChangeActions() {
  const dispatch = useDispatch();
  const salaryService = useWintSalaryService();
  const { getTargetGrossSalaryChangeDialog, getZeroGrossSalaryChangeDialog } = useGrossSalaryChangeDialog();

  const { canChangeGrossSalary } = useGrossSalaryChangePermissions();

  const onError = (error: any) => {
    if (!error) {
      return;
    }
    if (error instanceof Error) {
      Sentry.withScope((scope) => {
        scope.setTag("component", "salaryChangeActions");
        Sentry.captureException(error);
      });
    }
    dispatch(
      displayErrorMessage(
        LocalizationHelperInstance.getFormattedMessage(error?.message ?? TranslationKeys.Errors.SomethingWentWrong),
        {
          isGlobal: true,
        }
      )
    );
  };

  const buildSalaryChangeMenuItems = ({
    personId,
    yearMonth,
    personName,
    onActionSuccess,
    hiddenZero,
    hiddenAdjust,
  }: BuildSalaryChangeActionsArgs): MoreActionMenuItem[] => {
    const currentYearMonth = DateHelper.getYearMonth(DateHelper.today());
    const isCurrentMonth = yearMonth === currentYearMonth;

    const onZeroGrossSalaryChangeClick = async () => {
      return await salaryService.updateTargetGrossSalaryAmount(personId, yearMonth, 0);
    };

    return [
      {
        description: LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Salary.ZeroGrossSalaryChangeDialogResetSalary
        ),
        icon: IconDefinitionConstants.WTimesThin,
        actionDialog: {
          ...getZeroGrossSalaryChangeDialog(
            personId,
            yearMonth,
            personName,
            onZeroGrossSalaryChangeClick,
            onActionSuccess
          ),
          onError: onError,
        },
        hidden: hiddenZero || !canChangeGrossSalary(personId) || !isCurrentMonth,
      },
      {
        description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.AdjustSalary),
        icon: IconDefinitionConstants.WEdit,
        actionDialog: {
          ...getTargetGrossSalaryChangeDialog(personId, yearMonth, personName, onActionSuccess),
          onError: onError,
        },
        hidden: hiddenAdjust || !canChangeGrossSalary(personId) || !isCurrentMonth,
      },
    ];
  };

  return { buildSalaryChangeMenuItems };
}

export default useSalaryChangeActions;
